import PropTypes from "prop-types"
import { createElement, useState, useEffect } from "react"
import ReactMarkdown from "react-markdown"
import RehypeRaw from "rehype-raw"
import RehypeReact from "rehype-react"
import Info from "components/Info"
import Link from "components/Link"
import ResponsiveTable from "components/ResponsiveTable"
import YoutubeVideo from "components/YoutubeVideo"
import ContentCTA from "components/ui/ContentCTA"
import ImageCarousel from "components/ui/ImageCarousel"
import ImageWrapper from "components/ui/ImageWrapper"
import SideBySide from "components/ui/SideBySide"

/**
 * Custom Components
 *
 * We are using `htmlAst` field generated by gatsby-transformer-remark
 * to render React components from markdown. By doing so, we don't need
 * to use MDX anymore.
 *
 * Reference:
 * https://using-remark.gatsbyjs.org/custom-components/
 */

const htmlComponents = {
  a: ({ href, className, ...props }) => {
    if (href.startsWith("#")) {
      return <a href={href} className={className} {...props} />
    }
    return (
      <Link
        href={href}
        className={`text-lush ${className || ""}`}
        {...props}
        openNewTab
      />
    )
  },
  table: ({ children, ...props }) => (
    <ResponsiveTable {...props}>{children}</ResponsiveTable>
  ),
}

const customComponents = {
  ...htmlComponents,
  "youtube-video": YoutubeVideo,
  info: Info,
  cta: ContentCTA,
  imagecarousel: ImageCarousel,
  imagewrapper: ImageWrapper,
  sidebyside: SideBySide,
}

function MarkdownContent({
  className,
  rawMarkdownBody,
  htmlAst,
  fallbackHtml,
}) {
  const [renderAst, setRenderAst] = useState(null)

  useEffect(() => {
    if (htmlAst) {
      setRenderAst(
        new RehypeReact({ createElement, components: customComponents })
      )
    }
  }, [])

  const renderWithPlugins = () => {
    if (renderAst) {
      return renderAst.Compiler(htmlAst)
    }
    // Fallback to improve Core Web Vitals CLS metrics while Rehype process the content
    if (fallbackHtml) {
      return <div dangerouslySetInnerHTML={{ __html: fallbackHtml }} />
    }
    return null
  }

  const renderWithoutPlugins = () => (
    <ReactMarkdown components={htmlComponents} rehypePlugins={[RehypeRaw]}>
      {rawMarkdownBody}
    </ReactMarkdown>
  )

  return (
    <div className={className}>
      {htmlAst ? renderWithPlugins() : renderWithoutPlugins()}
    </div>
  )
}

MarkdownContent.propTypes = {
  /** Additional className(s) */
  className: PropTypes.string,
  /** Raw markdown without gatsby-remark-* plugins and custom components */
  rawMarkdownBody: PropTypes.string,
  /** HTML AST with gatsby-remark-* plugins and custom components */
  htmlAst: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /** HTML without custom components to be used as a fallback */
  fallbackHtml: PropTypes.string,
}

export default MarkdownContent
